import { getGameConfig } from '../config/gameConfig';
import type { Settlement } from '../engine/map/settlement';
import { recordConsumed, recordProduced } from '../engine/map/settlement';
import type { SettlementBuildingKey, SettlementInventory } from '../engine/settlement/types';
import {
    getBuildingStorage,
    getConfigBuildingKey,
    getEffectiveOutputCap,
} from '../engine/settlement/utils';

type ProductionBatch = {
    resource: NumericResourceKey;
    amount: number;
};

type ProductionIO = {
    inputs: ProductionBatch[];
    outputs: ProductionBatch[];
};

type NumericResourceKey = keyof Omit<SettlementInventory, 'tools'>;

function isNumericResource(resource: string): resource is NumericResourceKey {
    return resource !== 'tools';
}

function buildProductionIo(
    buildingId: SettlementBuildingKey,
    config = getGameConfig(),
): ProductionIO {
    const configKey = getConfigBuildingKey(buildingId);
    const input = config.buildings[configKey].input || {};
    const output = config.buildings[configKey].output || {};
    const inputs = Object.entries(input).reduce<ProductionBatch[]>((acc, [resource, entry]) => {
        if (!isNumericResource(resource)) return acc;
        acc.push({
            resource,
            amount: Math.max(1, entry.batch ?? 1),
        });
        return acc;
    }, []);
    const outputs = Object.entries(output).reduce<ProductionBatch[]>((acc, [resource, entry]) => {
        if (!isNumericResource(resource)) return acc;
        acc.push({
            resource,
            amount: Math.max(1, entry.batch ?? 1),
        });
        return acc;
    }, []);
    return { inputs, outputs };
}

function canStoreOutputs(
    settlement: Settlement,
    buildingId: SettlementBuildingKey,
    outputs: ProductionBatch[],
    config = getGameConfig(),
): boolean {
    const storage = getBuildingStorage(settlement, buildingId);
    for (const output of outputs) {
        const cap = getEffectiveOutputCap(settlement, config, buildingId, output.resource);
        if (cap <= 0) return false;
        if (storage[output.resource] >= cap) return false;
        if (cap - storage[output.resource] < output.amount) return false;
    }
    return true;
}

function hasInputs(
    settlement: Settlement,
    buildingId: SettlementBuildingKey,
    inputs: ProductionBatch[],
) {
    const storage = getBuildingStorage(settlement, buildingId);
    for (const input of inputs) {
        if (storage[input.resource] < input.amount) return false;
    }
    return true;
}

function consumeInputs(
    settlement: Settlement,
    buildingId: SettlementBuildingKey,
    inputs: ProductionBatch[],
) {
    const storage = getBuildingStorage(settlement, buildingId);
    for (const input of inputs) {
        storage[input.resource] -= input.amount;
        recordConsumed(settlement, input.resource as keyof typeof storage, input.amount);
    }
}

function produceOutputs(
    settlement: Settlement,
    buildingId: SettlementBuildingKey,
    outputs: ProductionBatch[],
) {
    const storage = getBuildingStorage(settlement, buildingId);
    for (const output of outputs) {
        storage[output.resource] += output.amount;
        recordProduced(settlement, output.resource as keyof typeof storage, output.amount);
    }
}

export function runIndependentProductionTick(
    settlement: Settlement,
    buildingId: SettlementBuildingKey,
    jobCount: number,
    progress: number[],
    ticksPerBatch: number,
): boolean {
    const building = settlement.buildings[buildingId];
    if (building.qty <= 0 || jobCount <= 0) {
        const hadWork = progress.length > 0;
        progress.length = 0;
        return hadWork;
    }

    let changed = false;
    while (progress.length < jobCount) {
        progress.push(0);
        changed = true;
    }
    while (progress.length > jobCount) {
        progress.pop();
        changed = true;
    }

    const { inputs, outputs } = buildProductionIo(buildingId);
    if (outputs.length === 0) return changed;

    for (let i = 0; i < progress.length; i += 1) {
        if (!hasInputs(settlement, buildingId, inputs)) break;
        if (!canStoreOutputs(settlement, buildingId, outputs)) break;
        progress[i] += 1;
        if (progress[i] >= ticksPerBatch) {
            progress[i] -= ticksPerBatch;
            consumeInputs(settlement, buildingId, inputs);
            produceOutputs(settlement, buildingId, outputs);
            changed = true;
        }
    }

    return changed;
}

export function runCooperativeProductionTick(
    settlement: Settlement,
    buildingId: SettlementBuildingKey,
    jobCount: number,
    workPerBatch: number,
    work: number,
): { changed: boolean; work: number } {
    const building = settlement.buildings[buildingId];
    if (building.qty <= 0 || jobCount <= 0) {
        const changed = work !== 0;
        return { changed, work: 0 };
    }

    const { inputs, outputs } = buildProductionIo(buildingId);
    if (outputs.length === 0) return { changed: false, work };

    if (!canStoreOutputs(settlement, buildingId, outputs)) {
        const changed = work !== 0;
        return { changed, work: 0 };
    }

    let changed = false;
    const prevWork = work;
    work += jobCount;
    if (work !== prevWork) changed = true;

    while (
        work >= workPerBatch &&
        hasInputs(settlement, buildingId, inputs) &&
        canStoreOutputs(settlement, buildingId, outputs)
    ) {
        work -= workPerBatch;
        consumeInputs(settlement, buildingId, inputs);
        produceOutputs(settlement, buildingId, outputs);
        changed = true;
    }

    return { changed, work };
}
